import { InteractionStatus } from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import React, { ReactNode, useMemo } from 'react';
import { OIDC_SCOPES, signOutFailed, signOutStarted } from '~/lib/auth';
import AuthSessionContext, { AuthSession } from './AuthSessionContext';

interface Props {
    children: ReactNode;
}

/**
 * Publishes the Azure Entra session to the application.
 *
 * Signing in is silent when the browser already holds a Microsoft session, and
 * redirects to Microsoft only when it does not. MSAL keeps its token cache in
 * session storage, so a new tab starts without tokens even for a signed-in
 * user; the silent attempt is what spares them a round trip to Microsoft.
 *
 * Must be mounted inside an `MsalProvider`; the msal-react hooks it uses have
 * no meaning without one.
 */
const MsalAuthSessionProvider: React.FC<Props> = ({ children }) => {
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const account = useAccount();

    const session = useMemo<AuthSession>(
        () => ({
            isAuthenticated,
            // msal-react reports no account until start-up has processed the
            // cache and any redirect response.
            isPending: inProgress !== InteractionStatus.None,
            userName: account?.name ?? account?.username,
            userSignInName: account?.username,
            logIn: async () => {
                const request = { scopes: OIDC_SCOPES };
                try {
                    await instance.ssoSilent(request);
                } catch {
                    // No Microsoft session the silent flow can use, or the
                    // user must interact (account choice, consent, MFA).
                    await instance.loginRedirect(request);
                }
            },
            switchAccount: async () => {
                // The current account stays cached until the sign-in returns:
                // start-up then makes the returned account the active one.
                // Microsoft's own session is kept, and still offered in its
                // account chooser.
                await instance.loginRedirect({
                    scopes: OIDC_SCOPES,
                    prompt: 'select_account',
                });
            },
            logOut: async () => {
                signOutStarted();
                try {
                    await instance.logoutRedirect({
                        account,
                        // Required by OIDC logout endpoints to identify the
                        // session to end; accepted and ignored by Entra.
                        idTokenHint: account?.idToken,
                        postLogoutRedirectUri: '/logout',
                    });
                } catch (error) {
                    signOutFailed();
                    throw error;
                }
            },
        }),
        [instance, inProgress, isAuthenticated, account]
    );

    return (
        <AuthSessionContext.Provider value={session}>
            {children}
        </AuthSessionContext.Provider>
    );
};

export default MsalAuthSessionProvider;
